import type { MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import {
  type MassTortMatterSearch,
  type ReportsMatterSearchTableRow,
} from "@/lib/types";
import { resolveReportPrimaryStatute } from "@/lib/utils/matters/report-statute-utils";

export type MassTortMatterSearchTableRow = MatterSearchResult &
  ReportsMatterSearchTableRow;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

export const mapMassTortMatterSearchToTableRow = (
  matter: MassTortMatterSearch,
): MassTortMatterSearchTableRow => {
  const assignees: ReportsMatterSearchTableRow["matterAssignee"] = [];
  matter.assignees.forEach((name) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    const [firstName, ...rest] = trimmed.split(" ");
    assignees.push({
      firstName: firstName,
      lastName: rest.join(" "),
    });
  });

  const clientEmails: NonNullable<
    ReportsMatterSearchTableRow["matterContactConnection"][number]["matterContactConnectionContactEmail"]
  > = [];
  matter.clientEmails.forEach((email) => {
    if (!isRecord(email)) {
      return;
    }

    const trimmed = getString(email.address);
    if (!trimmed) {
      return;
    }
    clientEmails.push({
      address: trimmed,
      description:
        typeof email.description === "string" ? email.description : null,
      id: typeof email.id === "string" ? email.id : "",
      isPrimary: typeof email.isPrimary === "boolean" ? email.isPrimary : false,
      label: typeof email.label === "string" ? email.label : null,
    });
  });

  const clientPhones: NonNullable<
    ReportsMatterSearchTableRow["matterContactConnection"][number]["matterContactConnectionContactPhone"]
  > = [];
  matter.clientPhones.forEach((phone) => {
    if (!isRecord(phone)) {
      return;
    }

    const trimmed = getString(phone.number);
    if (!trimmed) {
      return;
    }
    clientPhones.push({
      description:
        typeof phone.description === "string" ? phone.description : null,
      extension: typeof phone.extension === "string" ? phone.extension : null,
      id: typeof phone.id === "string" ? phone.id : "",
      isPrimary: typeof phone.isPrimary === "boolean" ? phone.isPrimary : false,
      label: typeof phone.label === "string" ? phone.label : null,
      number: trimmed,
    });
  });

  const primaryStatute = resolveReportPrimaryStatute(
    matter.statuteOfLimitations.map((statute) => ({
      ...statute,
      date: statute.date,
      description: statute.description ?? undefined,
    })),
    undefined,
  );

  const clientName = getString(matter.clientName);
  const hasClientContact =
    Boolean(clientName) || clientEmails.length > 0 || clientPhones.length > 0;

  return {
    claimTypes: matter.claimTypes,
    id: matter.id,
    firmId: matter.firmId,
    massTortId: matter.massTortId,
    title: matter.title ?? "",
    emailAddress: matter.emailAddress ?? "",
    dateOfLoss: matter.dateOfLoss ?? "",
    status: {
      id: matter.statusLabel ?? "",
      label: matter.statusLabel ?? "",
      categoryId: matter.statusCategory ?? "",
      category: {
        id: matter.statusCategory ?? "",
        label: matter.statusCategory ?? "",
      },
    },
    statutesOfLimitations: matter.statuteOfLimitations.map((statute) => ({
      ...statute,
      dateCreated: matter.dateCreated ?? "",
      dateModified: matter.dateModified ?? "",
      firmId: matter.firmId,
      id: statute.id,
      matterId: matter.id,
    })),
    incidentAddressCity: matter.incidentAddressCity ?? "",
    incidentAddressState: matter.incidentAddressState ?? "",
    incidentAddressStreet1: "",
    incidentAddressStreet2: "",
    incidentAddressZip: "",
    incidentAddressCountry: "",
    incidentType: {
      id: matter.incidentType ?? "",
      label: matter.incidentType ?? "",
      incidentTypeCategoryId: null,
    },
    matterId: matter.id,
    dateCreated: matter.dateCreated ?? "",
    dateModified: matter.dateModified ?? "",
    matterTitle: matter.title ?? "",
    matterStatusLabel: matter.statusLabel ?? "",
    matterStatusCategory: matter.statusCategory ?? "",
    matterDefendantName: matter.defendantNames,
    claimNumber: matter.insuranceClaimNumbers,
    policyNumber: matter.insurancePolicyNumbers,
    matterEmailAddress: matter.emailAddress ?? "",
    matterDateOfLoss: matter.dateOfLoss ?? "",
    matterStatuteOfLimitations: primaryStatute.matterStatuteOfLimitations,
    matterStatuteOfLimitationsConfirmed:
      primaryStatute.matterStatuteOfLimitationsConfirmed,
    matterIncidentAddressCity: matter.incidentAddressCity ?? "",
    matterIncidentAddressState: matter.incidentAddressState ?? "",
    matterIncidentAddressStreet1: "",
    matterIncidentAddressStreet2: "",
    matterIncidentAddressZip: "",
    matterIncidentAddressCountry: "",
    matterContactConnection: hasClientContact
      ? [
          {
            label: "Client",
            matterContactConnectionContactName: clientName || undefined,
            matterContactConnectionContactEmail: clientEmails,
            matterContactConnectionContactPhone: clientPhones,
          },
        ]
      : [],
    matterAssignee: assignees,
    matterTeamConnection: matter.teams.map((team) => ({
      label: team,
      matterTeamConnectionDateTimeEnd: undefined,
    })),
    matterTagConnection: matter.tags.map((tag) => ({
      label: tag,
    })),
    incidentTypeLabel: matter.incidentType ?? "",
  };
};
